/**
 * PWA 緩存管理工具
 * 負責計算緩存清除日、驗證訂閱狀態、管理預先緩存等
 */

interface CacheMetadata {
  clearDate: number // 緩存清除日（時間戳）
  subscriptionEndDate: number // 訂閱到期日（時間戳）
  lastVerifiedDate: number // 最後驗證日期（時間戳）
  preloadedCount: number // 已預先緩存的項目數量
  currentLevel: number // 當前等級（5=N5, 1=N1）
}

const CACHE_METADATA_KEY = 'pwa_cache_metadata'
const PRELOAD_COUNT = 200 // 預先緩存數量
const AUTO_LOAD_THRESHOLD = 50 // 自動載入觸發閾值
const AUTO_LOAD_COUNT = 50 // 每次自動載入數量
const BASE_CACHE_DAYS = 7 // 基礎緩存天數
const MAX_CACHE_DAYS_AFTER_EXPIRY = 2 // 訂閱到期後最多延長天數

/**
 * 獲取緩存元數據
 */
export function getCacheMetadata(): CacheMetadata | null {
  if (typeof window === 'undefined') return null
  
  try {
    const stored = localStorage.getItem(CACHE_METADATA_KEY)
    if (!stored) return null
    return JSON.parse(stored)
  } catch (error) {
    console.error('[PWA Cache] 讀取緩存元數據失敗:', error)
    return null
  }
}

/**
 * 保存緩存元數據
 */
export function saveCacheMetadata(metadata: CacheMetadata): void {
  if (typeof window === 'undefined') return
  
  try {
    localStorage.setItem(CACHE_METADATA_KEY, JSON.stringify(metadata))
  } catch (error) {
    console.error('[PWA Cache] 保存緩存元數據失敗:', error)
  }
}

/**
 * 計算緩存清除日
 * 規則：
 * 1. 基礎：登入日 + 7 天
 * 2. 限制：不能超過訂閱到期日 + 2 天
 */
export function calculateCacheClearDate(
  loginDate: number,
  subscriptionEndDate: number
): number {
  const baseClearDate = loginDate + BASE_CACHE_DAYS * 24 * 60 * 60 * 1000
  const maxClearDate = subscriptionEndDate + MAX_CACHE_DAYS_AFTER_EXPIRY * 24 * 60 * 60 * 1000
  
  // 取較小的值（不超過限制）
  return Math.min(baseClearDate, maxClearDate)
}

/**
 * 更新緩存清除日（用戶登入時調用）
 */
export function updateCacheClearDate(
  subscriptionEndDate: number,
  currentLevel: number
): void {
  const now = Date.now()
  const clearDate = calculateCacheClearDate(now, subscriptionEndDate)
  
  const metadata: CacheMetadata = {
    clearDate,
    subscriptionEndDate,
    lastVerifiedDate: now,
    preloadedCount: PRELOAD_COUNT, // 預先緩存前 200 個
    currentLevel
  }
  
  saveCacheMetadata(metadata)
}

/**
 * 檢查緩存狀態（僅用於記錄，不觸發自動清除）
 * 
 * ⚠️ 重要變更：此函數不再觸發自動清除緩存
 * 改為「阻擋使用，保留資料」的策略：
 * - 訂閱到期後，只阻擋使用（通過權限檢查）
 * - 不主動清除任何緩存或學習記錄
 * - 學習記錄永遠保留
 * 
 * 此函數現在只用於檢查狀態，不執行清除動作
 */
export function shouldClearCache(): {
  shouldClear: boolean
  reason: 'subscription_expired' | 'cache_expired' | 'none'
} {
  const metadata = getCacheMetadata()
  if (!metadata) {
    return { shouldClear: false, reason: 'none' }
  }
  
  const now = Date.now()
  
  // 計算訂閱過期日：到期日的下一天 00:00:00（本地時間）
  // 例如：到期日 11/26 → 過期日 11/27 00:00:00
  const subscriptionEndDate = new Date(metadata.subscriptionEndDate)
  
  // 獲取到期日的本地日期（不包含時間）
  const endYear = subscriptionEndDate.getFullYear()
  const endMonth = subscriptionEndDate.getMonth()
  const endDay = subscriptionEndDate.getDate()
  
  // 計算過期日：到期日的下一天 00:00:00（本地時間）
  const subscriptionExpiredDate = new Date(
    endYear,
    endMonth,
    endDay + 1, // 到期日 +1 天
    0, 0, 0, 0 // 00:00:00
  ).getTime()
  
  // ⚠️ 變更：不再返回 shouldClear: true
  // 即使訂閱已過期，也不自動清除緩存
  // 權限檢查會在 middleware 和 API routes 中處理
  if (now >= subscriptionExpiredDate) {
    // 只返回狀態，不觸發清除
    return { shouldClear: false, reason: 'subscription_expired' }
  }
  
  // ⚠️ 變更：不再返回 shouldClear: true
  // 即使緩存清除日到期，也不自動清除
  if (now >= metadata.clearDate) {
    // 只返回狀態，不觸發清除
    return { shouldClear: false, reason: 'cache_expired' }
  }
  
  return { shouldClear: false, reason: 'none' }
}

/**
 * 清除緩存（僅清除 API 和音檔緩存，不清除學習記錄）
 * 
 * ⚠️ 重要：此函數只清除可重新下載的緩存（API 響應和音檔），
 * 不會清除任何學習記錄（vocabularyProgress, kanjiProgress, grammarProgress, gojuonProgress）
 * 
 * 學習記錄永遠保留在 localStorage 中，不會被此函數清除。
 */
export async function clearPWACache(): Promise<void> {
  if (typeof window === 'undefined' || !('serviceWorker' in navigator)) {
    return
  }
  
  try {
    const registration = await navigator.serviceWorker.getRegistration()
    if (registration) {
      // 只清除 API 和音檔緩存（可重新下載的內容）
      // 不會清除學習記錄（localStorage 中的 progress 資料）
      const cacheNames = await caches.keys()
      await Promise.all(
        cacheNames.map(cacheName => {
          if (cacheName.includes('study-api') || cacheName.includes('audio-files')) {
            return caches.delete(cacheName)
          }
        })
      )
    }
    
    // 清除元數據（緩存管理用的 metadata，不影響學習記錄）
    localStorage.removeItem(CACHE_METADATA_KEY)
  } catch (error) {
    console.error('[PWA Cache] 清除緩存失敗:', error)
  }
}

/**
 * 檢查是否需要顯示到期提醒
 * 規則：訂閱到期前 3、2、1 天以及到期當天都要顯示提醒
 * 每天顯示一次（使用 localStorage 記錄已顯示的日期）
 */
export function shouldShowExpiryWarning(): boolean {
  const metadata = getCacheMetadata()
  if (!metadata) return false
  
  const now = Date.now()
  const subscriptionEndDate = new Date(metadata.subscriptionEndDate)
  const subscriptionEndTime = subscriptionEndDate.getTime()
  
  // 計算剩餘天數（向下取整，例如 2.5 天 = 2 天）
  // 如果已過期（剩餘天數 < 0），不顯示提醒
  const daysRemaining = Math.floor((subscriptionEndTime - now) / (24 * 60 * 60 * 1000))
  
  // 如果已過期（剩餘天數 < 0），不顯示提醒
  if (daysRemaining < 0) {
    return false
  }
  
  // 只在到期前 3、2、1 天以及到期當天（0 天）顯示提醒
  if (daysRemaining > 3) {
    return false
  }
  
  // 檢查今天是否已經顯示過提醒（避免重複顯示）
  const today = new Date(now)
  const todayKey = `${today.getFullYear()}-${today.getMonth()}-${today.getDate()}`
  const lastShownDate = typeof window !== 'undefined' 
    ? localStorage.getItem('subscription_expiry_warning_shown_date')
    : null
  
  // 如果今天已經顯示過，不再顯示
  if (lastShownDate === todayKey) {
    return false
  }
  
  return true
}

/**
 * 獲取當前已預先緩存的數量
 */
export function getPreloadedCount(): number {
  const metadata = getCacheMetadata()
  return metadata?.preloadedCount || 0
}

/**
 * 檢查是否需要自動背景載入
 * 規則：學習進度到「緩存數量 - 50」時觸發
 */
export function shouldAutoLoadMore(learnedCount: number): boolean {
  const preloadedCount = getPreloadedCount()
  const threshold = preloadedCount - AUTO_LOAD_THRESHOLD
  
  return learnedCount >= threshold
}

/**
 * 更新已預先緩存的數量（自動載入後調用）
 */
export function updatePreloadedCount(additionalCount: number): void {
  const metadata = getCacheMetadata()
  if (!metadata) return
  
  metadata.preloadedCount += additionalCount
  saveCacheMetadata(metadata)
}

/**
 * 驗證訂閱狀態並更新緩存清除日
 * 
 * ⚠️ 重要變更：此函數不再自動清除緩存
 * 即使訂閱已過期，也只更新狀態，不執行清除動作
 */
export async function verifyAndUpdateCache(
  subscriptionEndDate: number,
  currentLevel: number
): Promise<{
  shouldClear: boolean
  reason: 'subscription_expired' | 'cache_expired' | 'none'
  updated: boolean
}> {
  // 檢查緩存狀態（僅用於記錄，不觸發清除）
  const { reason } = shouldClearCache()
  
  // ⚠️ 變更：不再執行清除動作
  // 即使訂閱已過期，也不清除緩存
  // 權限檢查會在 middleware 和 API routes 中處理
  
  // 更新緩存清除日
  const metadata = getCacheMetadata()
  const now = Date.now()
  
  // 如果訂閱已過期，不更新清除日，但也不清除緩存
  if (now >= subscriptionEndDate) {
    return { shouldClear: false, reason: reason || 'subscription_expired', updated: false }
  }
  
  // 計算新的清除日
  const newClearDate = calculateCacheClearDate(now, subscriptionEndDate)
  
  // 如果已有元數據，檢查是否需要更新
  if (metadata) {
    // 如果新的清除日比舊的晚，更新
    if (newClearDate > metadata.clearDate) {
      updateCacheClearDate(subscriptionEndDate, currentLevel)
      return { shouldClear: false, reason: 'none', updated: true }
    }
    
    // 更新最後驗證日期
    metadata.lastVerifiedDate = now
    saveCacheMetadata(metadata)
    return { shouldClear: false, reason: 'none', updated: false }
  } else {
    // 沒有元數據，創建新的
    updateCacheClearDate(subscriptionEndDate, currentLevel)
    return { shouldClear: false, reason: 'none', updated: true }
  }
}

